import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// SheEarns Unified Dashboard (Mobile-first with bottom sheet modals)
// Reflect-a-Day, SkillQuest, and Shakti Wallet integrated with smooth mobile UX
// Refactored for dark theme with pink accents
// Added touch gestures: swipe down to dismiss bottom sheet

const STORAGE_KEY_WALLET = "sheearns_shakti_wallet_v1";

export default function SheEarnsDashboard() {
  const [wallet, setWallet] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_WALLET);
      return raw ? JSON.parse(raw) : { coins: 0, totalXP: 0, peacePoints: 0 };
    } catch {
      return { coins: 0, totalXP: 0, peacePoints: 0 };
    }
  });

  const [activeCard, setActiveCard] = useState(null);

  const cards = [
    {
      title: "🪞 Reflect-a-Day",
      desc: "Daily reflections to earn Peace Points & self-awareness.",
      color: "from-rose-900/20 to-rose-800/20",
      content: (
        <div className="p-4 text-sm text-slate-200">
          <h3 className="font-semibold mb-2 text-pink-300">Reflect-a-Day</h3>
          <p>Write your daily gratitude or reflection entry to earn Peace Points 🌸</p>
        </div>
      ),
    },
    {
      title: "💎 SkillQuest",
      desc: "Micro-learning quests to boost skills & earn XP.",
      color: "from-blue-900/20 to-blue-800/20",
      content: (
        <div className="p-4 text-sm text-slate-200">
          <h3 className="font-semibold mb-2 text-pink-300">SkillQuest</h3>
          <p>Complete micro-learning modules and earn XP Gems 💎 for progress.</p>
        </div>
      ),
    },
    {
      title: "💖 Shakti Wallet",
      desc: "View combined rewards, badges, and your progress level.",
      color: "from-pink-900/20 to-pink-800/20",
      content: (
        <div className="p-4 text-sm text-slate-200">
          <h3 className="font-semibold mb-2 text-pink-300">Shakti Wallet</h3>
          <p>Track your total XP, Peace Points, and Shakti Coins 🪙 all in one view.</p>
        </div>
      ),
    },
  ];

  const handleDragEnd = (e, info) => {
    if (info.offset.y > 100) { // Threshold for dismiss (adjust as needed)
      setActiveCard(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 p-4 md:p-6">
      <header className="max-w-md mx-auto text-center">
        <h1 className="text-2xl font-bold text-pink-300">🌸 SheEarns Dashboard</h1>
        <p className="text-sm text-slate-300 mt-1">Empower. Learn. Reflect. Rise.</p>

        <div className="mt-4 bg-slate-800/50 backdrop-blur-sm rounded-xl p-4 shadow-sm border border-slate-700/50">
          <div className="flex justify-between text-sm">
            <div className="text-left">
              <div className="text-xs text-slate-400">Total XP</div>
              <div className="font-semibold text-slate-200">{wallet.totalXP}</div>
            </div>
            <div className="text-center">
              <div className="text-xs text-slate-400">Peace Points</div>
              <div className="font-semibold text-slate-200">{wallet.peacePoints}</div>
            </div>
            <div className="text-right">
              <div className="text-xs text-slate-400">Shakti Coins</div>
              <div className="font-semibold text-pink-400">{wallet.coins}</div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-md mx-auto mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 place-items-center">
        {cards.map((c) => (
          <button
            key={c.title}
            onClick={() => setActiveCard(c)}
            className={`rounded-2xl bg-gradient-to-b ${c.color} p-4 shadow-lg hover:shadow-pink-500/10 hover:border-pink-500/20 border border-slate-700/50 transition-all text-left backdrop-blur-sm`}
          >
            <div className="text-lg font-semibold mb-1 text-slate-100">{c.title}</div>
            <p className="text-sm text-slate-300 mb-2">{c.desc}</p>
            <div className="text-right text-sm text-pink-400 font-medium">Open →</div>
          </button>
        ))}
      </main>

      <footer className="max-w-md mx-auto mt-8 text-center text-xs text-slate-500">
        Synced locally • Part of <span className="font-semibold text-pink-400">SheEarns</span> Empowerment Suite 🌼
      </footer>

      <AnimatePresence>
        {activeCard && (
          <motion.div
            className="fixed inset-0 bg-black/60 flex justify-center items-end z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActiveCard(null)}
          >
            <motion.div
              className="bg-slate-800/95 backdrop-blur-sm rounded-t-3xl w-full max-w-md p-4 shadow-2xl border-t border-slate-600 relative"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              drag="y"
              dragConstraints={{ top: 0 }}
              dragElastic={0.2}
              onDragEnd={handleDragEnd}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drag Handle */}
              <div className="flex justify-center mb-4 pt-2">
                <div className="w-8 h-1 bg-slate-500 rounded-full"></div>
              </div>
              
              <div className="flex justify-between items-center mb-2">
                <h2 className="text-lg font-semibold text-pink-300">{activeCard.title}</h2>
                <button
                  className="text-slate-400 text-sm hover:text-pink-400 transition-colors"
                  onClick={() => setActiveCard(null)}
                >
                  ✕
                </button>
              </div>
              {activeCard.content}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}